import React, { useState } from "react";

import {
  requestResendOtp,
  sendOtpVerification,
} from "../../../Api/Signin/Signin.Api";
import { Theme } from "../../../Style/Theme";
import useCountdownTimer from "../../../Utils/Hooks/useCountdownTimer";
import useErrorMessage from "../../../Utils/Hooks/useErrorMessage";
import useLoading from "../../../Utils/Hooks/useLoading";
import { formatTime } from "../../../Utils/TimeFormat/TimeFormat";
import { LoadingButtonType } from "../../atoms/LoadingButton/LoadingButton";
import { Heading } from "../../atoms/Typography/Heading/Heading";
import { SubHeader4 } from "../../atoms/Typography/SubHeader/SubHeader";
import { Text7, Text8, TextLink } from "../../atoms/Typography/Text/Text";
import { FormItemsWrap } from "../Wrap/Wrap";

export const OTPForm = ({
  onClick,
  length,
  attempt,
  t,
  openContactSupport,
  state,
  setAttempt,
}) => {
  const [otp, setOtp] = useState(Array(length).fill(""));
  const { error, showError, hideError } = useErrorMessage();
  const otpInputs = Array.from({ length });
  const { time, isActive, reset, start } = useCountdownTimer(
    state?.otpDuration,
  );

  const { createLoadingFunction } = useLoading();

  const loadingEvent1 = createLoadingFunction("verifyOtp");
  const loadingEvent2 = createLoadingFunction("resendOtp");

  const retryCount = process.env.REACT_APP_OTP_RETRY_COUNT;

  const handleInputChange = (index, value) => {
    if (error.show) hideError();

    if (!isNaN(value) && value?.length <= 1) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      if (value && index < length - 1) {
        otpInputs[index + 1].focus();
      }
    }
  };

  const handleInputKeyDown = (event, index) => {
    if (event.key === "Backspace" && !otp[index] && index > 0) {
      otpInputs[index - 1].focus();
    } else if (event.key === "Enter") {
      // Call your function here
      onSubmit();
    }
  };

  const handlePaste = (e) => {
    hideError();
    e.preventDefault();
    const data = e.clipboardData.getData("text").slice(0, length);
    const newOtpArray = data.split("");

    let newOTP = otp.map((item, index) => {
      if (!isNaN(newOtpArray[index])) {
        return newOtpArray[index];
      }
      else {
        return "";
      }
    });
    setOtp(newOTP);
    otpInputs[data.length - 1].focus();
  };

  const onSubmit = async () => {
    const otpCode = otp.join("");
    if (!otpCode) {
      showError(t("validation:required-field"));
    } else if (otpCode.length !== 6) {
      showError(t("validation:invalid-otp"));
    } else {
      loadingEvent1.start();
      await sendOtpVerification({
        emailAddress: state?.user?.email,
        otpCode: otpCode,
      })
        .then((e) => {
          if (e.type == 0) {
            onClick("pass");
          } else if (e.type == 2) {
            showError(t(e.message, { ns: "validation" }));
            onClick(e.message);
          } else {
            showError(t(e.message, { ns: "validation" }));
            onClick("fail");
          }
        })
        .catch((e) => {
          onClick("fail");
        });
      loadingEvent1.stop();
    }
  };
  return (
    <FormItemsWrap gap={error.show ? "27px" : "48px"} onPaste={handlePaste}>
      <FormItemsWrap direction={"column"} gap={"10px"}>
        <FormItemsWrap
          direction={"row"}
          gap={"10px"}
          justifyContent={"space-between"}
        >
          {otp.map((digit, index) => {
            return (
              <input
                style={{
                  height: "50px",
                  width: "50px",
                  borderRadius: "7px",
                  border: `1px solid ${
                    error.show ? Theme.palette.text.pink : "#8E8E93"
                  }`,
                  background: "#FFF",
                  textAlign: "center",
                }}
                key={index}
                type="tel"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                placeholder="-"
                onChange={(e) => handleInputChange(index, e.target.value)}
                onKeyDown={(e) => {
                  handleInputKeyDown(e, index);
                }}
                onPaste={handlePaste}
                ref={(input) => (otpInputs[index] = input)}
              />
            );
          })}
        </FormItemsWrap>
        {error.show && (
          <Text8 color={Theme.palette.text.pink} text={error.message} />
        )}
        {isActive && (
          <FormItemsWrap direction={"row"} justifyContent={"center"}>
            <Text7 text={t("glossary:auth.verification.otp-code-expires-in")} />
            <Heading
              text={formatTime(time)}
              fontSize={"11px"}
              letterSpacing={"0.066px"}
              lineHeight={"13px"}
            />
          </FormItemsWrap>
        )}
      </FormItemsWrap>
      <FormItemsWrap gap={"28px"}>
        <LoadingButtonType
          loading={loadingEvent1.isLoading()}
          disabled={attempt > retryCount}
          type="submit"
          variant={attempt > retryCount ? "contained" : "primaryBlue"}
          fullWidth
          onClick={onSubmit}
        >
          {t("common:button.next")}
        </LoadingButtonType>

        {!isActive && (
          <FormItemsWrap justifyContent={"center"} direction={"row"}>
            <SubHeader4
              text={t("glossary:auth.verification.code-not-received")}
            />
            <TextLink
              text={t("glossary:auth.verification.resend-code")}
              loading={loadingEvent2.isLoading()}
              onClick={async () => {
                if (attempt < retryCount) {
                  loadingEvent2.start();
                }
                if (attempt < retryCount) {
                  await requestResendOtp({
                    emailAddress: state?.user?.email,
                    lang: state?.lang?.code,
                  }).then((e) => {
                    if (e?.type == 0) {
                      onClick("resend");
                      // reset otp
                      setOtp(Array(length).fill(""));
                      reset();
                      // start otp
                      start();
                      // hide error
                      hideError();
                    } else if (e?.type == 2) {
                      if (e?.message === "contact-support") {
                        setAttempt(4);
                        openContactSupport();
                      } else showError(t(e?.message, { ns: "validation" }));
                      onClick("fail");
                    } else if (e?.type == 3) {
                      showError(t(e?.message, { ns: "validation" }));
                      onClick("fail");
                    }
                  });
                  loadingEvent2.stop();
                } else if (attempt >= retryCount) {
                  onClick("resend");
                  openContactSupport();
                }
              }}
            />
          </FormItemsWrap>
        )}
      </FormItemsWrap>
    </FormItemsWrap>
  );
};
